import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { BookUser, Loader2, Search, Utensils, Wallet } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import apiClient from '@/api/client'
import { ReceiveTabPaymentDialog, type TabSettlementExtras } from '@/components/counter/ReceiveTabPaymentDialog'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
import { parseAmountField } from '@/lib/houseAccounts'
import { printTabPaymentSlipForResult } from '@/lib/printTabPaymentSlip'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatCurrency, toIsoDate } from '@/lib/utils'
import type { HouseAccount, HouseAccountKind, HouseAccountStatus, TabRateIntent, TabSettlementResult, User } from '@/types'

/**
 * F-MM-N30 — enable and manage a guest's tab. Admin and manager write
 * (backend houseAccountsWrite); the reconciliation reports live in Reports →
 * Staff & Tabs.
 *
 * F-MM-N31: staff meal allowances moved to Manage Staff. An allowance made on
 * this customer before the move shows here as one line pointing there.
 */
export function CustomerHouseAccountsPanel({ customerID }: { customerID: string }) {
  const gate = useHouseAccountsEnabled()
  const queryClient = useQueryClient()
  const accountsQuery = useQuery({
    queryKey: ['house-accounts', 'customer', customerID],
    queryFn: () => apiClient.listHouseAccounts({ customer_id: customerID }),
  })
  // A closed tab that still owes stays on screen so the money can be collected.
  const accounts = (accountsQuery.data?.data ?? []).filter(
    (a) =>
      (a.status !== 'closed' || (a.kind === 'tab' && a.balance > 0.004)) &&
      (a.kind === 'tab' ? gate.tabsVisible : gate.staff),
  )
  const live = (accountsQuery.data?.data ?? []).filter((a) => a.status !== 'closed')
  const has = (k: HouseAccountKind) => live.some((a) => a.kind === k)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
    queryClient.invalidateQueries({ queryKey: ['counter-house-accounts'] })
    queryClient.invalidateQueries({ queryKey: ['counter-house-account'] })
  }

  if (accountsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading accounts…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {accounts.map((a) =>
        a.kind === 'staff_allowance' ? (
          <MovedToStaffNote key={a.id} account={a} />
        ) : (
          <AccountCard key={a.id} account={a} onChanged={invalidate} />
        ),
      )}
      {gate.tabs && !has('tab') ? <EnableForm customerID={customerID} kind="tab" onCreated={invalidate} /> : null}
    </div>
  )
}

function MovedToStaffNote({ account }: { account: HouseAccount }) {
  const navigate = useNavigate()
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-4 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Utensils className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          <span className="font-semibold">Staff meal allowance</span>
          <span className="text-muted-foreground"> — now managed in Manage Staff, under {account.person_name}.</span>
        </span>
      </div>
      <Button size="sm" variant="outline" onClick={() => void navigate({ to: '/admin/staff' })}>
        Open Manage Staff
      </Button>
    </div>
  )
}

function userLabel(u: Pick<User, 'first_name' | 'last_name' | 'username'>): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username
}

/**
 * Staff login picker. The users list is capped at 100 per page server-side,
 * so this searches instead of listing everyone.
 */
export function StaffLoginPicker({
  value,
  valueLabel,
  onChange,
}: {
  value: string
  valueLabel?: string
  onChange: (id: string, label: string) => void
}) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])
  const users = useQuery({
    queryKey: ['users', 'house-account-link', debounced],
    queryFn: () => apiClient.getUsers({ per_page: 20, search: debounced || undefined }),
  })
  const rows: User[] = users.data?.data ?? []

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">
          {value ? <>Linked: <span className="font-medium text-foreground">{valueLabel || 'staff login'}</span></> : 'Not linked'}
        </span>
        {value ? (
          <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => onChange('', '')}>
            Unlink
          </Button>
        ) : null}
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          placeholder="Search staff by name or username"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 pl-9"
        />
      </div>
      <div className="max-h-40 space-y-1 overflow-y-auto">
        {users.isFetching && rows.length === 0 ? (
          <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Searching…
          </div>
        ) : rows.length === 0 ? (
          <p className="py-2 text-xs text-muted-foreground">No staff match.</p>
        ) : (
          rows.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onChange(u.id, userLabel(u))}
              className={`flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-sm ${
                u.id === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
            >
              <span className="truncate">{userLabel(u)}</span>
              <span className="text-xs text-muted-foreground">{u.role}</span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

function RatePicker({ rate, onChange }: { rate: TabRateIntent; onChange: (r: TabRateIntent) => void }) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {(['card', 'cash'] as const).map((r) => (
        <Button key={r} type="button" size="sm" variant={rate === r ? 'default' : 'outline'} onClick={() => onChange(r)}>
          {r === 'card' ? 'Card rate' : 'Cash rate'}
        </Button>
      ))}
    </div>
  )
}

function EnableForm({
  customerID,
  kind,
  onCreated,
}: {
  customerID: string
  kind: HouseAccountKind
  onCreated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [touched, setTouched] = useState(false)
  const [rate, setRate] = useState<TabRateIntent>('card')
  const [userID, setUserID] = useState('')
  const [userName, setUserName] = useState('')
  const isStaff = kind === 'staff_allowance'
  const parsed = parseAmountField(amount)

  const create = useMutation({
    mutationFn: async () => {
      const n = parsed.value ?? 0
      const res = await apiClient.createHouseAccount({
        customer_id: customerID,
        kind,
        monthly_allowance: isStaff ? n : undefined,
        credit_limit: isStaff ? undefined : n,
        tab_rate_intent: isStaff ? undefined : rate,
        user_id: isStaff && userID ? userID : undefined,
      })
      if (!res.success) throw new Error(res.message || 'Could not enable the account')
      return res
    },
    onSuccess: () => {
      toastHelpers.success(isStaff ? 'Staff meals enabled' : 'Tab enabled', '')
      setOpen(false)
      onCreated()
    },
    onError: (e: Error) => toastHelpers.error('Account', e.message),
  })

  if (!open) {
    return (
      <Button variant="outline" className="w-full justify-start border-dashed" onClick={() => setOpen(true)}>
        {isStaff ? <Utensils className="mr-2 h-4 w-4" aria-hidden /> : <BookUser className="mr-2 h-4 w-4" aria-hidden />}
        {isStaff ? 'Enable staff meals' : 'Enable a tab'}
      </Button>
    )
  }

  const submit = () => {
    setTouched(true)
    if (parsed.error) return
    create.mutate()
  }

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="text-sm font-semibold">{isStaff ? 'Staff meal allowance' : 'Customer tab'}</div>
      <p className="text-xs text-muted-foreground">
        {isStaff
          ? 'Meals are charged at menu value with no tax or service charge, and are not counted as sales. Stock still comes off. Every meal needs a manager PIN; going over the allowance is allowed and logged.'
          : 'A normal taxed sale, filed on the day it is eaten, paid later. Card-rate tabs can only be paid off by card or online.'}
      </p>
      <label className="block space-y-1 text-sm">
        <span className="font-medium">{isStaff ? 'Monthly allowance' : 'Credit limit (0 = none)'}</span>
        <Input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onBlur={() => setTouched(true)}
          aria-invalid={(touched && Boolean(parsed.error)) || undefined}
          className="h-9"
        />
        {touched && parsed.error ? <span className="block text-xs text-destructive">{parsed.error}</span> : null}
      </label>
      {isStaff ? (
        <div className="space-y-1 text-sm">
          <span className="font-medium">Staff login (optional)</span>
          <StaffLoginPicker
            value={userID}
            valueLabel={userName}
            onChange={(id, label) => {
              setUserID(id)
              setUserName(label)
            }}
          />
        </div>
      ) : (
        <div className="space-y-1 text-sm">
          <span className="font-medium">Tax rate</span>
          <RatePicker rate={rate} onChange={setRate} />
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="sm" disabled={create.isPending} onClick={submit}>
          {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
          Enable
        </Button>
      </div>
    </div>
  )
}

function statusBadge(a: HouseAccount): { label: string; variant: 'secondary' | 'outline' | 'destructive' } {
  const owes = a.kind === 'tab' && a.balance > 0.004
  if (a.status === 'closed') return { label: owes ? 'Closed — still owes' : 'Closed', variant: owes ? 'destructive' : 'outline' }
  if (a.status === 'suspended') return { label: owes ? 'Suspended — still owes' : 'Suspended', variant: owes ? 'destructive' : 'outline' }
  return { label: 'Active', variant: 'secondary' }
}

type EditMode = null | 'amount' | 'rate' | 'user'

function AccountCard({ account, onChanged }: { account: HouseAccount; onChanged: () => void }) {
  const queryClient = useQueryClient()
  const isStaff = account.kind === 'staff_allowance'
  const [edit, setEdit] = useState<EditMode>(null)
  const [amount, setAmount] = useState(String(isStaff ? account.monthly_allowance : account.credit_limit))
  const [amountTouched, setAmountTouched] = useState(false)
  const [rate, setRate] = useState<TabRateIntent>(account.tab_rate_intent)
  const [userID, setUserID] = useState(account.user_id ?? '')
  const [userName, setUserName] = useState(account.user_name ?? '')
  const [confirmClose, setConfirmClose] = useState(false)
  const [showStatement, setShowStatement] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const parsed = parseAmountField(amount)
  const badge = statusBadge(account)
  const owes = !isStaff && account.balance > 0.004

  const startEdit = (mode: EditMode) => {
    setAmount(String(isStaff ? account.monthly_allowance : account.credit_limit))
    setAmountTouched(false)
    setRate(account.tab_rate_intent)
    setUserID(account.user_id ?? '')
    setUserName(account.user_name ?? '')
    setEdit(mode)
  }

  const update = useMutation({
    mutationFn: async (body: Parameters<typeof apiClient.updateHouseAccount>[1]) => {
      const res = await apiClient.updateHouseAccount(account.id, body)
      if (!res.success) throw new Error(res.message || 'Could not update the account')
      return res
    },
    onSuccess: () => {
      setEdit(null)
      setConfirmClose(false)
      onChanged()
    },
    onError: (e: Error) => toastHelpers.error('Account', e.message),
  })

  const saveAmount = () => {
    setAmountTouched(true)
    if (parsed.value == null) return
    update.mutate(isStaff ? { monthly_allowance: parsed.value } : { credit_limit: parsed.value })
  }

  const handleSettled = (result: TabSettlementResult, _account: HouseAccount, extras: TabSettlementExtras) => {
    onChanged()
    queryClient.invalidateQueries({ queryKey: ['house-accounts', 'statement', account.id] })
    void printTabPaymentSlipForResult(queryClient, result, extras, formatCurrency)
      .then((printed) => {
        if (!printed) toastHelpers.error('Slip not printed', 'The payment is saved. Check the printer and pop-up settings.')
      })
      .catch(() => toastHelpers.error('Slip not printed', 'The payment is saved. Check the printer.'))
  }

  return (
    <div className="space-y-2 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {isStaff ? <Utensils className="h-4 w-4" aria-hidden /> : <BookUser className="h-4 w-4" aria-hidden />}
          {isStaff ? 'Staff meals' : 'Tab'}
          {account.user_name ? <span className="font-normal text-muted-foreground">· {account.user_name}</span> : null}
        </div>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </div>

      {isStaff ? (
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Fact label="Allowance" value={formatCurrency(account.monthly_allowance)} />
          <Fact label="Used this month" value={formatCurrency(account.allowance_used)} />
          <Fact label="Over this month" value={formatCurrency(account.overage_this_month)} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Fact label="Owes" value={formatCurrency(account.balance)} />
          <Fact label="Credit limit" value={account.credit_limit > 0 ? formatCurrency(account.credit_limit) : 'None'} />
          <Fact label="Tax rate" value={account.tab_rate_intent === 'card' ? 'Card' : 'Cash'} />
        </div>
      )}

      {owes ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-sm">
          <span>
            {account.customer_name} owes <span className="font-semibold tabular-nums">{formatCurrency(account.balance)}</span>
          </span>
          <Button size="sm" onClick={() => setPayOpen(true)}>
            <Wallet className="mr-2 h-4 w-4" aria-hidden />
            Receive payment
          </Button>
        </div>
      ) : null}

      {edit === 'amount' ? (
        <div className="space-y-1">
          <div className="flex items-end gap-2">
            <label className="flex-1 space-y-1 text-sm">
              <span className="font-medium">{isStaff ? 'Monthly allowance' : 'Credit limit (0 = none)'}</span>
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-invalid={(amountTouched && Boolean(parsed.error)) || undefined}
                className="h-9"
              />
            </label>
            <Button size="sm" disabled={update.isPending} onClick={saveAmount}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>
              Cancel
            </Button>
          </div>
          {amountTouched && parsed.error ? <p className="text-xs text-destructive">{parsed.error}</p> : null}
        </div>
      ) : edit === 'rate' ? (
        <div className="space-y-2 text-sm">
          <span className="font-medium">Tax rate for this tab</span>
          <RatePicker rate={rate} onChange={setRate} />
          <p className="text-xs text-muted-foreground">Applies to future bills only; bills already on the tab keep their rate.</p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={update.isPending || rate === account.tab_rate_intent}
              onClick={() => update.mutate({ tab_rate_intent: rate })}
            >
              Save
            </Button>
          </div>
        </div>
      ) : edit === 'user' ? (
        <div className="space-y-2 text-sm">
          <span className="font-medium">Staff login</span>
          <StaffLoginPicker
            value={userID}
            valueLabel={userName}
            onChange={(id, label) => {
              setUserID(id)
              setUserName(label)
            }}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={update.isPending || userID === (account.user_id ?? '')}
              onClick={() => update.mutate({ user_id: userID })}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {account.status !== 'closed' ? (
            <Button size="sm" variant="outline" onClick={() => startEdit('amount')}>
              {isStaff ? 'Change allowance' : 'Change limit'}
            </Button>
          ) : null}
          {account.status !== 'closed' && !isStaff ? (
            <Button size="sm" variant="outline" onClick={() => startEdit('rate')}>
              Change tax rate
            </Button>
          ) : null}
          {account.status !== 'closed' && isStaff ? (
            <Button size="sm" variant="outline" onClick={() => startEdit('user')}>
              Staff login
            </Button>
          ) : null}
          {!isStaff ? (
            <Button size="sm" variant="outline" onClick={() => setShowStatement((v) => !v)}>
              {showStatement ? 'Hide statement' : 'Statement'}
            </Button>
          ) : null}
          {account.status === 'active' ? (
            <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ status: 'suspended' })}>
              Suspend
            </Button>
          ) : account.status === 'suspended' ? (
            <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ status: 'active' })}>
              Reactivate
            </Button>
          ) : null}
          {account.status !== 'closed' ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={update.isPending}
              onClick={() => setConfirmClose(true)}
            >
              Close
            </Button>
          ) : null}
        </div>
      )}

      {showStatement && !isStaff ? <TabStatement account={account} /> : null}

      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="sm:max-w-sm" role="alertdialog">
          <DialogHeader>
            <DialogTitle>Close this {isStaff ? 'staff meal account' : 'tab'}?</DialogTitle>
            <DialogDescription>
              {isStaff
                ? `${account.customer_name} can no longer have meals charged. History stays in the reports.`
                : owes
                  ? `${account.customer_name} still owes ${formatCurrency(account.balance)} — a tab with money owed cannot be closed. Take the payment first.`
                  : `${account.customer_name} can no longer charge bills to a tab. History stays in the reports.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>
              Keep it open
            </Button>
            <Button
              variant="destructive"
              disabled={update.isPending || owes}
              onClick={() => update.mutate({ status: 'closed' as HouseAccountStatus })}
            >
              {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              Close account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isStaff ? (
        <ReceiveTabPaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          preselectedAccountId={account.id}
          onSettled={handleSettled}
        />
      ) : null}
    </div>
  )
}

const ENTRY_LABEL: Record<string, string> = {
  charge: 'Bill',
  charge_reversal: 'Credit note',
  settlement: 'Payment',
  adjustment: 'Adjustment',
}

/** Every bill and payment on the tab since it was opened, with the running balance. */
function TabStatement({ account }: { account: HouseAccount }) {
  const from = account.created_at.slice(0, 10)
  const to = toIsoDate(new Date())
  const statement = useQuery({
    queryKey: ['house-accounts', 'statement', account.id, from, to],
    queryFn: () => apiClient.getHouseAccountStatement(account.id, { from, to }),
  })
  const data = statement.data?.data

  if (statement.isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading statement…
      </div>
    )
  }
  if (statement.isError || !data) {
    return (
      <div className="space-y-2 py-2 text-sm">
        <p className="text-destructive">{(statement.error as Error)?.message || 'Could not load the statement.'}</p>
        <Button size="sm" variant="outline" onClick={() => void statement.refetch()}>
          Retry
        </Button>
      </div>
    )
  }
  if (data.entries.length === 0) {
    return <p className="py-2 text-sm text-muted-foreground">Nothing on this tab yet.</p>
  }
  return (
    <div className="max-h-80 overflow-y-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Entry</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.entries.map((e) => (
            <TableRow key={e.id}>
              <TableCell className="whitespace-nowrap tabular-nums">{e.business_date ?? e.created_at.slice(0, 10)}</TableCell>
              <TableCell>
                {ENTRY_LABEL[e.entry_type] ?? e.entry_type}
                {e.order_number ? <span className="ml-1 font-mono text-xs">#{e.order_number}</span> : null}
                {e.method ? <span className="text-muted-foreground"> · {e.method}</span> : null}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(e.amount)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatCurrency(e.running_balance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex justify-between border-t px-4 py-2 text-sm font-semibold">
        <span>Owes now</span>
        <span className="tabular-nums">{formatCurrency(data.closing_balance)}</span>
      </div>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  )
}
